import type { FC, ReactNode } from 'react'
import type { LegalSection } from '@/components/sections/legal-page'

import Link from 'next/link'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { LegalPage } from '@/components/sections/legal-page'
import { siteConfig } from '@/constants/site'

const SECTIONS: Array<LegalSection> = [
    {
        id: 'information-we-collect',
        title: 'Information we collect',
        body: (
            <>
                <p>We collect only what we need to run Veyra and to respond to you:</p>
                <ul>
                    <li><strong>Account details</strong> such as your name, work email, company and plan when you create an account. Passwords are stored only as secure, one-way hashes.</li>
                    <li><strong>Form submissions</strong> such as contact messages and demo requests, including the details you choose to share.</li>
                    <li><strong>Financial data you connect</strong> through read-only integrations once you start using the product, such as transactions, balances and invoices.</li>
                    <li><strong>Usage information</strong> such as pages visited and the device and browser used, collected without cookies and without identifying you personally.</li>
                </ul>
            </>
        )
    },
    {
        id: 'how-we-use-information',
        title: 'How we use information',
        body: (
            <>
                <p>We use your information to provide and improve Veyra, to respond to enquiries and demo requests, to keep your account secure and to send you messages you have asked for, such as replies to your questions.</p>
                <p>Your financial data is used only to deliver the service to your organization. It is never sold, and it is never used to train models that serve other customers.</p>
            </>
        )
    },
    {
        id: 'emails',
        title: 'Emails and tracking',
        body: (
            <p>Some emails we send may include a small tracking image and tracked links that tell us whether a message was opened or a link was clicked. We use this only to understand whether our messages are useful. Open tracking is approximate, and you can block it by turning off automatic image loading in your email app.</p>
        )
    },
    {
        id: 'cookies',
        title: 'Cookies',
        body: (
            <p>Our website analytics do not use cookies. When you log in, we set a single essential cookie that keeps you signed in securely. It cannot be read by scripts and is removed when you sign out or after 30 days.</p>
        )
    },
    {
        id: 'sharing',
        title: 'Sharing with service providers',
        body: (
            <p>We work with a small number of trusted providers for hosting, email delivery and secure account connections. They process data only on our instructions and under confidentiality and security obligations. We do not sell personal information.</p>
        )
    },
    {
        id: 'security',
        title: 'Security',
        body: (
            <p>Data is encrypted in transit and at rest, connections to your financial accounts are read-only, and access inside Veyra is controlled with role-based permissions and recorded in an audit trail.</p>
        )
    },
    {
        id: 'retention',
        title: 'Retention',
        body: (
            <p>We keep account and financial data for as long as your account is active and for a limited period afterwards where required by law. Contact and demo requests are kept for as long as needed to respond and follow up, then deleted.</p>
        )
    },
    {
        id: 'your-rights',
        title: 'Your rights',
        body: (
            <p>Depending on where you live, you may have the right to access, correct, export or delete your personal information, or to object to certain processing. To make a request, <Link href='/contact'>contact us</Link> and we will respond within 30 days.</p>
        )
    },
    {
        id: 'contact',
        title: 'Contact',
        body: (
            <p>Questions about this policy can be sent to <a href={`mailto:${siteConfig.contact.email}`}>{siteConfig.contact.email}</a> or through our <Link href='/contact'>contact page</Link>.</p>
        )
    }
]

const Privacy: FC = (): ReactNode => {
    return (
        <Layout cta={false}>
            <Seo title='Privacy policy' description='How Veyra collects, uses and protects your information.' />

            <LegalPage
                eyebrow='Legal'
                title='Privacy policy'
                updated='October 9, 2026'
                intro={<p>This policy explains what information Veyra collects when you visit our website or use our product, how we use it and the choices you have.</p>}
                sections={SECTIONS}
            />
        </Layout>
    )
}

export default Privacy
